/**
 * GroundPlaneScale — metric grounding for a monocular SLAM map.
 *
 * Monocular SLAM has no scale: the map is correct up to an unknown factor, and
 * that factor is chosen arbitrarily at initialisation from whatever parallax the
 * tracker happened to see. Two sessions in the same physical room can disagree by
 * an order of magnitude (we measured 0.195 vs 0.577 "units per second walked" in
 * two captures of comparable walks). Anything placed at a fixed unit depth
 * therefore appears at a different real distance every session.
 *
 * The cheapest fix that needs no hardware and no participant interaction:
 *
 *   1. detect the floor plane (AlvaAR's findPlane)
 *   2. measure the camera's height above it — in SLAM units
 *   3. assume that height is ~1.5 m in the real world
 *   4. scale = assumedHeight / measuredHeight
 *
 * Accuracy is bounded by how well the assumption holds — roughly ±15%, dominated
 * by how people actually hold a phone rather than by stature. That is good enough
 * to stop fragments landing in your face one session and on the horizon the next,
 * which is the failure this exists to prevent. For better than that you need a
 * fiducial of known size (see ScaleSource below).
 *
 * Robustness comes from taking a MEDIAN over many observations and refusing to
 * commit until they agree. A single plane detection is not trustworthy: RANSAC
 * can lock onto a table top, a wall, or a reflection.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type ScaleState = 'unknown' | 'estimating' | 'locked' | 'rejected';

export interface ScaleEstimate {
  state: ScaleState;
  /** Multiply a SLAM-unit length by this to get metres. Null until estimated. */
  metersPerUnit: number | null;
  /** Median camera height above the floor, in SLAM units. */
  heightUnits: number | null;
  /** Accepted observations so far. */
  samples: number;
  /** Interquartile spread as a fraction of the median — the disagreement measure. */
  spread: number | null;
}

/**
 * Where a metric scale can come from. Ground plane + assumed height is the
 * always-available default implemented here.
 *
 * FUTURE — fiducial source (deliberately not implemented yet):
 * An AprilTag/ArUco marker of known printed size gives scale to ~1-2% instead of
 * ~15%, and — more valuable for this project — a SHARED ORIGIN. Two participants
 * who both see the same tag are in the same coordinate frame, which is what
 * "semantic spatial grounding across visitors" actually requires. For a gallery
 * installation the marker is a poster by the entrance, so the usual objection
 * (nobody carries a fiducial) does not apply.
 *
 * Candidate libraries, all permissively licensed so they stay clear of the GPL
 * problem AlvaAR creates: apriltag-js-standalone (BSD-2, WASM), js-aruco2 (MIT),
 * jsQR (Apache-2.0, QR only — coarser corners, so worse pose than AprilTag).
 *
 * Implement as a second ScaleSource and prefer it whenever a tag is visible,
 * falling back to the ground plane when it is not.
 */
export interface ScaleSource {
  estimate(): ScaleEstimate;
  reset(): void;
}

/** Default assumed height of a hand-held phone above the floor, in metres. */
export const DEFAULT_CAMERA_HEIGHT_M = 1.5;

/** Need at least this many agreeing observations before reporting a scale. */
const MIN_SAMPLES = 8;
/** Stop updating once this many have agreed — the estimate is as good as it gets. */
const LOCK_SAMPLES = 24;
/** Keep at most this many recent observations. */
const WINDOW = 40;
/** Reject the estimate while the interquartile spread exceeds this fraction. */
const MAX_SPREAD = 0.35;
/** Heights outside this range (SLAM units) are not a floor. */
const MIN_HEIGHT_UNITS = 1e-4;
const MAX_HEIGHT_UNITS = 1e4;
/**
 * How vertical the detected plane's normal must be to count as a floor.
 * Lenient on purpose: 0.5 is 60 degrees off vertical, which still excludes walls
 * while tolerating error in the normal we extract from AlvaAR's plane matrix
 * (see `planeFromAlvaMatrix` for how that axis was established).
 */
export const MIN_NORMAL_VERTICALITY = 0.5;

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function median(sorted: number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = n >> 1;
  return n % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export class GroundPlaneScale implements ScaleSource {
  private heights: number[] = [];
  private locked: ScaleEstimate | null = null;
  private rejectedNormals = 0;

  constructor(private assumedHeightM: number = DEFAULT_CAMERA_HEIGHT_M) {}

  /** Change the assumed height and re-derive. Invalidates any lock. */
  setAssumedHeight(metres: number): void {
    if (!(metres > 0) || !Number.isFinite(metres)) return;
    this.assumedHeightM = metres;
    this.locked = null;
  }

  get assumedHeight(): number {
    return this.assumedHeightM;
  }

  /** Observations rejected because the plane was not horizontal (walls, tables). */
  get rejected(): number {
    return this.rejectedNormals;
  }

  /**
   * Feed one plane detection.
   *
   * `worldUp` must be a unit vector pointing up IN THE SLAM FRAME. The SLAM world
   * frame has an arbitrary orientation, so this has to be derived from the IMU —
   * it cannot be assumed to be (0,1,0). `planeNormal` is advisory: when supplied
   * it is used only to reject non-horizontal planes, never to measure with.
   */
  observe(o: {
    planePoint: Vec3;
    planeNormal: Vec3 | null;
    cameraPos: Vec3;
    worldUp: Vec3;
  }): boolean {
    if (this.locked) return false;

    if (o.planeNormal) {
      const verticality = Math.abs(dot(o.planeNormal, o.worldUp));
      if (verticality < MIN_NORMAL_VERTICALITY) {
        this.rejectedNormals++;
        return false;
      }
    }

    // Perpendicular distance from the camera to the plane, along up. Absolute
    // value so a sign-convention error degrades to "still works" rather than
    // "silently negative".
    const d: Vec3 = {
      x: o.cameraPos.x - o.planePoint.x,
      y: o.cameraPos.y - o.planePoint.y,
      z: o.cameraPos.z - o.planePoint.z,
    };
    const h = Math.abs(dot(d, o.worldUp));
    if (!Number.isFinite(h) || h < MIN_HEIGHT_UNITS || h > MAX_HEIGHT_UNITS) return false;

    this.heights.push(h);
    if (this.heights.length > WINDOW) this.heights.shift();
    return true;
  }

  estimate(): ScaleEstimate {
    if (this.locked) return this.locked;

    const n = this.heights.length;
    if (n === 0) {
      return { state: 'unknown', metersPerUnit: null, heightUnits: null, samples: 0, spread: null };
    }

    const sorted = [...this.heights].sort((a, b) => a - b);
    const med = median(sorted);
    const q1 = sorted[Math.floor(n * 0.25)]!;
    const q3 = sorted[Math.floor(n * 0.75)]!;
    const spread = med > 0 ? (q3 - q1) / med : Infinity;

    if (n < MIN_SAMPLES) {
      return { state: 'estimating', metersPerUnit: null, heightUnits: med, samples: n, spread };
    }
    if (spread > MAX_SPREAD) {
      // Observations disagree — a table top and a floor, or a drifting map.
      // Report rather than commit to a wrong number.
      return { state: 'rejected', metersPerUnit: null, heightUnits: med, samples: n, spread };
    }

    const est: ScaleEstimate = {
      state: n >= LOCK_SAMPLES ? 'locked' : 'estimating',
      metersPerUnit: this.assumedHeightM / med,
      heightUnits: med,
      samples: n,
      spread,
    };
    if (est.state === 'locked') this.locked = est;
    return est;
  }

  reset(): void {
    this.heights = [];
    this.locked = null;
    this.rejectedNormals = 0;
  }
}

/**
 * A floor candidate from `AlvaAR.findPlane()`'s 16 floats, in the app's
 * (WebGL: Y up, Z back) frame, or null when the result is unusable.
 *
 * ## Which axis is the normal — MEASURED, not inferred
 *
 * The previous code took the normal from `mat[4..6]` on the strength of an
 * inference from AlvaAR's example. That was wrong, and it was the main reason
 * `metersPerUnit` never locked. Established 2026-10-04 by running the vendored
 * `alva_ar.js` itself (Node, no browser) over the frames of two real captures
 * (iPhone 12 Pro `…1787910541123`, Oppo `…1787910076884`), calling
 * `findPlane(250)` every 10 tracked frames, and comparing every candidate axis
 * with up carried across from the recorded IMU quaternion exactly as
 * `AlvaARBackend.worldUp()` does:
 *
 * | axis (memory order)          | iPhone median n·up | Oppo median n·up | floors kept |
 * |------------------------------|--------------------|------------------|-------------|
 * | `mat[0..2]`  ← this          | **1.000**          | **1.000**        | 55% / 94%   |
 * | `mat[4..6]`  (old code)      | 0.000              | 0.000            | ~3%         |
 * | `mat[1,5,9]` (column 1)      | 0.000              | 0.000            | ~3%         |
 * | Y of the pose's quaternion   | 0.000              | 0.000            | ~3%         |
 *
 * So the old extraction rejected nearly every floor as a wall, and the only
 * observations that ever reached the estimator were the degenerate ones below,
 * which skipped the verticality test entirely.
 *
 * ## Degenerate results are refused
 *
 * In the same runs **~30% of iPhone `findPlane` results had a NaN rotation**
 * with a finite translation. The old code turned a NaN normal into "normal
 * unknown" and accepted the plane unconditionally. A fit that cannot say which
 * way it faces is not evidence of a floor; it is refused here.
 *
 * The point is the translation, with the same Y/Z negation as the camera pose.
 */
export function planeFromAlvaMatrix(
  mat: ArrayLike<number> | null | undefined,
): { point: Vec3; normal: Vec3 } | null {
  if (!mat || mat.length < 16) return null;
  for (let i = 0; i < 16; i++) if (!Number.isFinite(mat[i]!)) return null;
  const point: Vec3 = { x: mat[12]!, y: -mat[13]!, z: -mat[14]! };
  // OpenCV -> WebGL for a direction: (x, -y, -z).
  const n: Vec3 = { x: mat[0]!, y: -mat[1]!, z: -mat[2]! };
  const len = Math.hypot(n.x, n.y, n.z);
  if (!(len > 1e-6)) return null;
  return { point, normal: { x: n.x / len, y: n.y / len, z: n.z / len } };
}

/** Whether a unit plane normal is close enough to `up` to be a floor. */
export function isHorizontalPlane(normal: Vec3, worldUp: Vec3): boolean {
  return Math.abs(dot(normal, worldUp)) >= MIN_NORMAL_VERTICALITY;
}
